import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { processPersonalAccidentCPR, transformVistaCPRData } from "../../../../api/cprServices";
import { getPersonalAccidentQuote } from "../../../../api/personalAccidentServices";
import { checkPartyCode } from "../../../../api/quoteServices";
import Input from "../../../../components/common/Input/Input";
import FileUploadInput from "../../../../components/common/newFileUploadInput/FileUploadInput";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../components/common/SingleDatePicker/SingleDatePicker";
import FormTabs from "../../../../components/common/tabs/formTabs";
import TelInput from "../../../../components/common/telInput/telInput";
import TravelDetailsTable from "../../../../components/common/TravelDetailsTable/TravelDetailsTable";
import { getLoggedInUserDataFromLocalStorage } from "../../../../config/config";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../../hooks/useScreenResolution";
import usePersonalAccidentFlow from "../../../../hooks/usePersonalAccidentFlow";
import usePersonalAccidentForm from "../../../../hooks/usePersonalAccidentFormData";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import {
  selectPersonalAccidentForm,
  setPersonalAccidentFormData,
} from "../../../../redux/features/personalAccidentFormSlice";
import {
  fetchProfileInfo,
  selectProfileData,
} from "../../../../redux/features/profileSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import PersonalAccidentJourneyLayout from "../../layouts/PersonalAccidentJourneyLayout";
import PersonalAccidentSummaryModal from "../../modals/personalAccidentSummaryModal";

// Gender tabs
const genderTabs = [
  { label_en: "Male", label_ar: "ذكر", type: "Male" },
  { label_en: "Female", label_ar: "أنثى", type: "Female" },
];

export const PERSONAL_ACCIDENT_CONTACT_DETAILS_FORM =
  "PERSONAL_ACCIDENT_CONTACT_DETAILS_FORM";
const PERSONAL_ACCIDENT_BASIC_INFO = "PERSONAL_ACCIDENT_BASIC_INFO";

const ContactDetails = () => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const screenResolution = useScreenResolution();
  const { handleNextSubstep } = usePersonalAccidentFlow();
  const dispatch = useDispatch();
  const profileData = useSelector(selectProfileData);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const personalAccidentFormData = useSelector(selectPersonalAccidentForm);

  // Always use English numerals, detect RTL for currency positioning
  const isRTL = i18n.resolvedLanguage === "ar";

  // Quote data state
  const [quoteData, setQuoteData] = useState(null);
  const [selfQuoteCache, setSelfQuoteCache] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [hasCalledSomeoneElseQuote, setHasCalledSomeoneElseQuote] = useState(false);

  // Helper to calculate age from a date string
  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Determine if user is guest (same logic as travel insurance)
  const isGuest = !Boolean(loginTokenInfo);

  // Fetch profile data if not already loaded and user is logged in
  useEffect(() => {
    if (!isGuest && !profileData) {
      dispatch(fetchProfileInfo());
    }
  }, [isGuest, profileData, dispatch]);

  // Nationality data from Redux
  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);

  // Fetch nationalities list on mount
  useEffect(() => {
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length, nationalitiesLoading]);

  // Fetch quote data when page loads
  useEffect(() => {
    const fetchQuote = async () => {
      try {
        // Check if quote data already exists - prevent infinite loop
        const existingQuoteData =
          personalAccidentFormData?.["PERSONAL_ACCIDENT_QUOTE_DATA"];
        if (existingQuoteData) {
          console.log("Quote data already exists, using cached data");
          setQuoteData(existingQuoteData);
          return;
        }

        setIsLoadingQuote(true);

        // Get basic info from Redux
        const basicInfoData =
          personalAccidentFormData?.[PERSONAL_ACCIDENT_BASIC_INFO];
        if (!basicInfoData) {
          console.error("No basic info data available");
          setIsLoadingQuote(false);
          return;
        }

        // Get user info from login token
        const userInfo =
          loginTokenInfo || getLoggedInUserDataFromLocalStorage();
        if (!userInfo || !userInfo.cpr) {
          console.error("User not logged in or CPR not available");
          setIsLoadingQuote(false);
          return;
        }

        // Get authentication token
        const authToken = userInfo.access_token || userInfo.token;
        if (!authToken) {
          console.error("No authentication token available");
          setIsLoadingQuote(false);
          return;
        }

        // Get party code
        let partyCode = null;
        if (userInfo.partyCode) {
          partyCode = userInfo.partyCode;
        } else {
          try {
            const partyResponse = await checkPartyCode(userInfo.cpr);
            partyCode = partyResponse?.data?.PartyCode;
          } catch (error) {
            console.error("Party code check failed:", error);
            setIsLoadingQuote(false);
            return;
          }
        }

        if (!partyCode) {
          console.error("No party code available");
          setIsLoadingQuote(false);
          return;
        }

        const age = userInfo.date_of_birth
          ? calculateAge(userInfo.date_of_birth)
          : 30;

        // Get sum insured from policy limit
        const policyLimitString = basicInfoData.policy_limit || "";
        const sumInsured = policyLimitString.replace(/[^0-9]/g, "");

        // Get occupation code
        const occupation = parseInt(basicInfoData.occupation);

        console.log(
          "=== Calling Personal Accident Quote API (Contact Details) ==="
        );
        console.log("Parameters:", {
          age,
          occupation,
          partyCode,
          sumInsured,
          hasToken: !!authToken,
        });

        const quoteResponse = await getPersonalAccidentQuote({
          age,
          occupation,
          partyCode,
          sumInsured,
          token: authToken,
        });

        console.log("Quote API Response:", quoteResponse);
        setQuoteData(quoteResponse);

        // Save quote data to Redux for use in Review & Pay page
        dispatch(
          setPersonalAccidentFormData({
            form_name: "PERSONAL_ACCIDENT_QUOTE_DATA",
            data: quoteResponse,
          })
        );
      } catch (error) {
        console.error("Get Quote Error:", error);
      } finally {
        setIsLoadingQuote(false);
      }
    };

    fetchQuote();
  }, [personalAccidentFormData, loginTokenInfo, dispatch]);

  // Personal accident form data hooks
  const { savePersonalAccidentDataInRedux, redux_form_data } =
    usePersonalAccidentForm(PERSONAL_ACCIDENT_CONTACT_DETAILS_FORM, () => {});

  // Helper function to create default passenger
  const createDefaultPassenger = (id) => ({
    id,
    cprNumber: "",
    fullName: "",
    emailAddress: "",
    mobileNumber: "",
    nationality: "", // Empty for placeholder
    gender: "", // No default gender selection
    dateOfBirth: {
      day: "",
      month: "",
      year: "",
      fullDate: "",
    },
    cprUploaded: false,
    cprFiles: [],
    // CPR processing state
    cprProcessing: false,
    cprData: null,
    cprProcessed: false,
  });

  const [passengers, setPassengers] = useState(() => [
    createDefaultPassenger(1),
  ]);

  // State for logged-in user selection
  const [policyFor, setPolicyFor] = useState(
    isGuest ? "someone_else" : "myself"
  );

  // State to track touched fields and form validation attempts
  const [touchedFields, setTouchedFields] = useState({});
  const [hasValidationAttempt, setHasValidationAttempt] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasValidatedData, setHasValidatedData] = useState(false);

  // Helper function to format profile date
  const formatProfileDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
    } catch {
      return dateString;
    }
  };

  // Helper function to normalize gender display
  const normalizeGender = (gender) => {
    if (!gender) return "Male"; // Default fallback
    
    // Normalize gender to full names
    const genderString = gender.toString().toLowerCase();
    if (genderString === "m" || genderString === "male") {
      return "Male";
    } else if (genderString === "f" || genderString === "female") {
      return "Female";
    }
    
    // Return as-is if already proper format, default to Male if unknown
    return gender === "Male" || gender === "Female" ? gender : "Male";
  };

  // Helper function to parse profile date for passenger form
  const parseProfileDateForForm = (dateString) => {
    if (!dateString) return { day: "", month: "", year: "", fullDate: "" };
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();
      const fullDate = `${year}-${month}-${day}`;
      return {
        day,
        month,
        year,
        fullDate,
      };
    } catch {
      return { day: "", month: "", year: "", fullDate: "" };
    }
  };

  // Real logged-in user data from profile
  const loggedInUserData = {
    cprNumber: profileData?.CPR || "",
    fullName: profileData
      ? `${profileData.FirstName || ""} ${profileData.LastName || ""}`.trim()
      : "",
    nationality: profileData?.Nationality || "Bahraini",
    gender: normalizeGender(profileData?.Gender),
    dateOfBirth: profileData?.DateOfBirth || profileData?.DOB ? formatProfileDate(profileData.DateOfBirth || profileData.DOB) : "",
    email: profileData?.Email || "",
    mobile: profileData?.PhoneNumber || "",
  };

  // Helper function to get nationality code from string name
  const getNationalityCode = (nationalityString) => {
    if (!nationalityString) return ""; // Return empty for placeholder

    // If it's already a number, return it
    if (typeof nationalityString === "number") return nationalityString;

    // Try to find the nationality in the options
    const found = nationalityOptions?.find(
      (opt) => opt.value?.toUpperCase() === nationalityString?.toUpperCase()
    );

    return found ? found.code : ""; // Return empty if not found
  };

  // Helper function to create passenger from logged-in user data
  const createLoggedInPassenger = (id) => {
    const passenger = {
      id,
      cprNumber: loggedInUserData.cprNumber,
      fullName: loggedInUserData.fullName,
      emailAddress: loggedInUserData.email,
      mobileNumber: loggedInUserData.mobile,
      nationality: getNationalityCode(loggedInUserData.nationality),
      gender: normalizeGender(profileData?.Gender), // Use normalized gender directly
      dateOfBirth: parseProfileDateForForm(profileData?.DateOfBirth || profileData?.DOB),
      cprUploaded: true, // Assume logged-in user has verified CPR
      cprFiles: [], // Empty files for logged-in user
      passengerType: "primary", // Mark as primary passenger
    };

    return passenger;
  };

  // Build travel data for displaying logged-in user info
  const buildLoggedInUserTravelData = () => {
    const data = {
      [translate("CPR Number", "رقم الهوية الوطنية")]:
        loggedInUserData.cprNumber,
      [translate("Full Name", "الاسم الكامل")]: loggedInUserData.fullName,
      [translate("Nationality", "الجنسية")]: translate(
        loggedInUserData.nationality,
        "بحريني"
      ),
      [translate("Gender", "الجنس")]: translate(loggedInUserData.gender, "ذكر"),
    };

    // Always show Date of Birth field, empty if no data
    data[translate("Date of Birth", "تاريخ الميلاد")] =
      loggedInUserData.dateOfBirth || "";

    return data;
  };

  // Handle policy selection change
  const handlePolicyForChange = (selection) => {
    // CRITICAL: Prevent tab switching while quote API is loading
    if (isLoadingQuote) {
      console.log("❌ Cannot switch tabs - quote API is loading");
      toast.warning(
        translate(
          "Please wait for the quote to finish loading before switching.",
          "يرجى الانتظار حتى ينتهي تحميل التسعيرة قبل التبديل."
        )
      );
      return;
    }

    setPolicyFor(selection);

    // Reset validation flag when switching policy types
    setHasValidatedData(false);

    if (selection === "myself" && !isGuest) {
      // Set logged-in user as primary passenger
      const loggedInPassenger = createLoggedInPassenger(1);
      setPassengers([loggedInPassenger]);
      // For "myself", set validated to true immediately (logged-in data is pre-filled)
      setHasValidatedData(true);
      // Restore cached "myself" quote
      setQuoteData(selfQuoteCache);
    } else {
      // Cache the current "myself" quote before clearing
      if (policyFor === "myself" && quoteData) {
        setSelfQuoteCache(quoteData);
      }
      // Clear quote so "myself" quote is not shown for "someone_else"
      setQuoteData(null);
      // Reset auto-quote flag so it re-fetches for the new passenger
      setHasCalledSomeoneElseQuote(false);
      // Reset to default passengers for "someone else" or guest
      setPassengers([createDefaultPassenger(1)]);
    }

    // Clear field errors when changing selection
    setFieldErrors({});
  };

  // Initialize passengers based on saved data
  useEffect(() => {
    // Restore saved contact details if available
    if (
      redux_form_data &&
      redux_form_data.passengers &&
      redux_form_data.passengers.length > 0
    ) {
      setPassengers(redux_form_data.passengers);
      setPolicyFor(
        redux_form_data.policyFor || (isGuest ? "someone_else" : "myself")
      );
      return; // Exit early to prevent further initialization
    } else {
      // Initialize with defaults
      setPolicyFor(isGuest ? "someone_else" : "myself");

      if (!isGuest) {
        const loggedInPassenger = createLoggedInPassenger(1);
        setPassengers([loggedInPassenger]);
      } else {
        const defaultPassenger = createDefaultPassenger(1);
        setPassengers([defaultPassenger]);
      }
    }
  }, [redux_form_data, isGuest]); // Depend on redux_form_data and isGuest

  // Auto-validate data for "someone_else" flow
  useEffect(() => {
    // Only for someone_else flow
    if (policyFor !== "someone_else" || hasValidatedData) {
      return;
    }

    // Must have at least one passenger
    if (passengers.length === 0) return;

    const passenger = passengers[0];

    // Check if all required fields are filled
    const isDataComplete =
      passenger.cprNumber?.trim() &&
      passenger.fullName?.trim() &&
      passenger.emailAddress?.trim() &&
      passenger.mobileNumber?.trim() &&
      passenger.dateOfBirth?.fullDate?.trim() &&
      passenger.gender?.trim() &&
      passenger.nationality;

    if (!isDataComplete) {
      console.log("Data incomplete - not validating");
      return;
    }

    // Extra check for "someone_else": Ensure nationality is a valid number code (not text)
    if (typeof passenger.nationality !== 'number' && isNaN(Number(passenger.nationality))) {
      console.log("Nationality is not a valid code:", passenger.nationality);
      return;
    }

    // Validate the passenger data
    const passengerErrors = validatePassengerFields(passenger);
    const hasErrors = Object.keys(passengerErrors).length > 0;

    if (hasErrors) {
      console.log("Someone Else data has validation errors:", passengerErrors);
      return;
    }

    // All fields valid - mark as validated
    console.log("✅ All fields valid for Someone Else");
    setHasValidatedData(true);
  }, [passengers, policyFor, hasValidatedData]);

  // Auto-fetch quote for "someone_else" when all fields are valid (like travel insurance pattern)
  useEffect(() => {
    if (policyFor !== "someone_else" || hasCalledSomeoneElseQuote || isLoadingQuote) {
      return;
    }

    if (!hasValidatedData) return;

    const fetchSomeoneElseQuote = async () => {
      setHasCalledSomeoneElseQuote(true);
      setIsLoadingQuote(true);

      try {
        const passenger = passengers[0];
        const basicInfoData = personalAccidentFormData?.[PERSONAL_ACCIDENT_BASIC_INFO];

        // For "Someone Else", do NOT use the logged-in user's partyCode.
        // Look up the party code from the insured person's CPR instead;
        // falls back to FALCON (guest/new customer) if no party exists.
        const userInfo = loginTokenInfo || getLoggedInUserDataFromLocalStorage();
        const authToken = userInfo?.access_token || userInfo?.token;

        let partyCode = "FALCON";
        try {
          const partyResponse = await checkPartyCode(passenger.cprNumber);
          if (partyResponse?.data?.PartyCode) {
            partyCode = partyResponse.data.PartyCode;
          }
        } catch (error) {
          console.error("Party code check failed for someone else CPR:", error);
        }

        // Use passenger's DOB to calculate their age
        const age = passenger.dateOfBirth?.fullDate
          ? calculateAge(passenger.dateOfBirth.fullDate)
          : 30;

        const policyLimitString = basicInfoData?.policy_limit || "";
        const sumInsured = policyLimitString.replace(/[^0-9]/g, "");
        const occupation = parseInt(basicInfoData?.occupation);

        console.log("=== Auto-fetching Quote for Someone Else ===");
        console.log("Parameters:", { age, occupation, partyCode, sumInsured });

        const quoteResponse = await getPersonalAccidentQuote({
          age,
          occupation,
          partyCode,
          sumInsured,
          token: authToken,
        });

        console.log("Someone Else Quote Response:", quoteResponse);
        setQuoteData(quoteResponse);

        dispatch(
          setPersonalAccidentFormData({
            form_name: "PERSONAL_ACCIDENT_QUOTE_DATA",
            data: quoteResponse,
          })
        );
      } catch (error) {
        console.error("Get Quote Error (someone else):", error);
      } finally {
        setIsLoadingQuote(false);
      }
    };

    fetchSomeoneElseQuote();
  }, [hasValidatedData, policyFor, hasCalledSomeoneElseQuote, isLoadingQuote]);

  // Helper function to get field error for a specific passenger
  const getFieldError = (passengerIndex, field) => {
    const fieldKey = `${field}_${passengerIndex}`;
    return fieldErrors[fieldKey] || "";
  };

  // Mobile date picker handler
  const handleMobileDatePickerSelect = (passengerIndex, dateString) => {
    if (!dateString) return;

    // Parse the date string (YYYY-MM-DD format from mobile date picker)
    const [year, month, day] = dateString.split("-");

    // Update state
    const updatedDOB = {
      day,
      month,
      year,
      fullDate: dateString,
    };
    handleInputChange(passengerIndex, "dateOfBirth", updatedDOB);

    // Validate date of birth in real-time when complete date is provided
    if (day && month && year) {
      const updatedPassenger = {
        ...passengers[passengerIndex],
        dateOfBirth: updatedDOB,
      };
      const validationErrors = validatePassengerFields(updatedPassenger);
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        newErrors[`dateOfBirth.day_${passengerIndex}`] = validationErrors["dateOfBirth.day"] || "";
        return newErrors;
      });
    }
  };

  // Custom validation function with proper priority
  const validatePassengerFields = (passenger) => {
    const newErrors = {};

    // CPR validation - required first, then format
    if (!passenger.cprNumber?.trim()) {
      newErrors.cprNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (passenger.cprNumber.length !== 9) {
      newErrors.cprNumber = translate(
        "CPR number must be 9 digits.",
        "يجب أن يتكون رقم الهوية الوطنية من 9 أرقام."
      );
    }

    // Full name validation - required first, then format
    if (!passenger.fullName?.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[a-zA-Z\u0600-\u06FF\s\.]+$/.test(passenger.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
      );
    }

    // Email validation - required first, then format
    if (!passenger.emailAddress?.trim()) {
      newErrors.emailAddress = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (
      !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(passenger.emailAddress)
    ) {
      newErrors.emailAddress = translate(
        "Invalid email address.",
        "عنوان البريد الإلكتروني غير صالح."
      );
    }

    // Mobile validation - required first, then length and numeric check
    if (!passenger.mobileNumber?.trim()) {
      newErrors.mobileNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (
      !/^\d+$/.test(passenger.mobileNumber) ||
      passenger.mobileNumber.length !== 8
    ) {
      newErrors.mobileNumber = translate(
        "Invalid mobile number.",
        "رقم الهاتف المحمول غير صحيح."
      );
    }

    // Nationality validation - required only (nationality is a number code, not string)
    if (!passenger.nationality) {
      newErrors.nationality = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Gender validation - required only
    if (!passenger.gender?.trim()) {
      newErrors.gender = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Date of birth validation - check if date is provided
    if (!passenger.dateOfBirth.fullDate?.trim()) {
      newErrors["dateOfBirth.day"] = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Date of birth future validation
      const birthDate = new Date(passenger.dateOfBirth.fullDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (birthDate > today) {
        newErrors["dateOfBirth.day"] = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
        );
      } else {
        // Check if user is at least 18 years old
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();

        // Adjust age if birthday hasn't occurred this year yet
        const isBeforeBirthday =
          monthDifference < 0 ||
          (monthDifference === 0 && today.getDate() < birthDate.getDate());

        const actualAge = isBeforeBirthday ? age - 1 : age;

        if (actualAge < 18) {
          newErrors["dateOfBirth.day"] = translate(
            "You must be at least 18 years old.",
            "يجب أن تكون 18 عامًا على الأقل."
          );
        }
      }
    }

    return newErrors;
  };

  const handleInputChange = (passengerIndex, field, value) => {
    let processedValue = value;

    // Apply input formatting based on field type
    if (field === "fullName") {
      // Full Name: Allow alphabetic characters, spaces, and periods
      processedValue = value;
    }

    setPassengers((prevPassengers) => {
      const updatedPassengers = [...prevPassengers];
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [parentField]: {
            ...updatedPassengers[passengerIndex][parentField],
            [childField]: processedValue,
          },
        };
      } else {
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [field]: processedValue,
        };
      }

      return updatedPassengers;
    });

    // Validate field after state update
    setTimeout(() => {
      const updatedPassengers = [...passengers];
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [parentField]: {
            ...updatedPassengers[passengerIndex][parentField],
            [childField]: processedValue,
          },
        };
      } else {
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [field]: processedValue,
        };
      }

      const updatedPassenger = updatedPassengers[passengerIndex];
      const passengerErrors = validatePassengerFields(updatedPassenger);

      // Create field key with passenger index for unique identification
      const fieldKey = `${field}_${passengerIndex}`;

      // Update field errors
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [fieldKey]: passengerErrors[field] || "",
      }));
    }, 0);
  };

  const handleFileSelect = async (fileData, passengerIndex) => {
    // Mark CPR field as touched for this passenger
    setTouchedFields((prev) => ({
      ...prev,
      [`cpr_${passengerIndex}`]: true,
    }));

    // Update passenger with uploaded file
    setPassengers((prevPassengers) => {
      const updatedPassengers = [...prevPassengers];
      const currentFiles = updatedPassengers[passengerIndex].cprFiles || [];

      // Add new file to the array
      updatedPassengers[passengerIndex] = {
        ...updatedPassengers[passengerIndex],
        cprFiles: [...currentFiles, fileData],
        cprUploaded: true,
        cprProcessing: true, // Start processing state
      };

      return updatedPassengers;
    });

    // Clear any field errors when files are uploaded successfully
    setFieldErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[`cpr_${passengerIndex}`]; // Remove the error completely
      return updatedErrors;
    });

    // Process CPR with OCR in background
    if (fileData && fileData.file) {
      try {
        console.log(
          "Starting CPR processing for passenger",
          passengerIndex + 1
        );

        const result = await processPersonalAccidentCPR(fileData.file);

        if (result.success && result.data?.output?.status) {
          // CPR processing successful
          console.log(
            "CPR processing completed successfully for passenger",
            passengerIndex + 1,
            result.data
          );

          const cprData = transformVistaCPRData(result.data);

          // CRITICAL: Convert nationality string to numeric code
          let nationalityCode = passengers[passengerIndex]?.nationality || ""; // Keep previous value by default

          if (cprData.nationality) {
            const nationalityString = cprData.nationality.toString().trim();

            // Check if it's Bahraini nationality (handles "BAHRAINI", "BHR", etc.)
            const isBahraini =
              nationalityString === "14" ||
              nationalityString.toLowerCase().includes("bahrain") ||
              nationalityString.toLowerCase().includes("بحرين") ||
              nationalityString.toUpperCase() === "BHR";

            // Try to find the nationality in options
            const foundNationality = nationalityOptions?.find(
              (opt) => {
                const optValue = opt.value?.toString().toLowerCase();
                const optLabel = opt.label?.toString().toLowerCase();
                const optCode = opt.code?.toString();
                const optId = opt.id?.toString();
                const searchString = nationalityString.toLowerCase();

                return (
                  optValue === searchString ||
                  optLabel === searchString ||
                  optCode === nationalityString ||
                  optId === nationalityString ||
                  (optLabel?.includes("bahrain") && isBahraini) ||
                  (optValue?.includes("bahrain") && isBahraini) ||
                  (optCode === "14" && isBahraini) ||
                  (optLabel?.includes("bhr") && nationalityString.toUpperCase() === "BHR") ||
                  (optValue?.includes("bhr") && nationalityString.toUpperCase() === "BHR")
                );
              }
            );

            if (foundNationality) {
              nationalityCode = foundNationality.code || foundNationality.id || foundNationality.value;
              console.log(`✅ Converted nationality "${nationalityString}" to code: ${nationalityCode}`);
            } else {
              nationalityCode = nationalityString;
              console.log(`⚠️ Couldn't find nationality code for: ${nationalityString}, using OCR value`);
            }
          }

          // Auto-fill passenger data with CPR information
          setPassengers((prevPassengers) => {
            const updatedPassengers = [...prevPassengers];
            updatedPassengers[passengerIndex] = {
              ...updatedPassengers[passengerIndex],
              // Auto-fill fields from CPR data
              cprNumber:
                cprData.cprNumber ||
                updatedPassengers[passengerIndex].cprNumber,
              fullName:
                cprData.fullName || updatedPassengers[passengerIndex].fullName,
              dateOfBirth: cprData.dateOfBirth
                ? {
                    ...cprData.dateOfBirth,
                    fullDate:
                      cprData.dateOfBirth.day &&
                      cprData.dateOfBirth.month &&
                      cprData.dateOfBirth.year
                        ? `${
                            cprData.dateOfBirth.year
                          }-${cprData.dateOfBirth.month.padStart(
                            2,
                            "0"
                          )}-${cprData.dateOfBirth.day.padStart(2, "0")}`
                        : "",
                  }
                : updatedPassengers[passengerIndex].dateOfBirth,
              gender:
                cprData.gender || updatedPassengers[passengerIndex].gender,
              nationality:
                nationalityCode || // Use converted numeric code
                updatedPassengers[passengerIndex].nationality,
              // Update processing state
              cprProcessing: false,
              cprData: cprData,
              cprProcessed: true,
            };
            return updatedPassengers;
          });

          toast.success(
            translate(
              "CPR processed successfully!",
              "تم معالجة بطاقة الهوية بنجاح!"
            )
          );
        } else {
          // CPR processing failed
          console.warn(
            "CPR processing failed for passenger",
            passengerIndex + 1,
            result.error || result.data
          );

          setPassengers((prevPassengers) => {
            const updatedPassengers = [...prevPassengers];
            updatedPassengers[passengerIndex] = {
              ...updatedPassengers[passengerIndex],
              cprProcessing: false,
            };
            return updatedPassengers;
          });

          // Show error toast for consistency with other flows
          toast.error(
            translate(
              "Failed to process CPR. Please fill the form manually.",
              "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
            )
          );
        }
      } catch (error) {
        console.error(
          "CPR processing error for passenger",
          passengerIndex + 1,
          error
        );

        setPassengers((prevPassengers) => {
          const updatedPassengers = [...prevPassengers];
          updatedPassengers[passengerIndex] = {
            ...updatedPassengers[passengerIndex],
            cprProcessing: false,
          };
          return updatedPassengers;
        });

        // Show error toast for consistency with other flows
        toast.error(
          translate(
            "Failed to process CPR. Please fill the form manually.",
            "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
          )
        );
      }
    }
  };

  const handleFileRemove = (passengerIndex) => {
    // Mark CPR field as touched for this passenger
    setTouchedFields((prev) => ({
      ...prev,
      [`cpr_${passengerIndex}`]: true,
    }));

    // Clear CPR data and processing state
    setPassengers((prevPassengers) => {
      const updatedPassengers = [...prevPassengers];
      updatedPassengers[passengerIndex] = {
        ...updatedPassengers[passengerIndex],
        cprUploaded: false,
        cprFiles: [],
        cprProcessing: false,
        cprData: null,
        cprProcessed: false,
        // Don't clear form data - let user decide if they want to keep it
      };
      return updatedPassengers;
    });

    // Set field error when files are removed (if field was touched or validation attempted)
    const fieldTouched = touchedFields[`cpr_${passengerIndex}`];
    if (fieldTouched || hasValidationAttempt) {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [`cpr_${passengerIndex}`]: translate("This field is required.", "هذا الحقل مطلوب.")
      }));
    }
  };

  // Function to check if CPR files are uploaded for a specific passenger
  const getCprUploadError = (passengerIndex) => {
    const passenger = passengers[passengerIndex];

    // Skip CPR validation for logged-in user when "For Myself" is selected
    // (first passenger is the logged-in user and already authenticated)
    if (!isGuest && policyFor === "myself" && passengerIndex === 0) {
      return "";
    }

    // Check if there's a specific field error for this CPR upload field
    const fieldErrorKey = `cpr_${passengerIndex}`;
    if (fieldErrors[fieldErrorKey]) {
      return fieldErrors[fieldErrorKey];
    }

    // Only show error if field has been touched OR validation has been attempted
    const fieldTouched = touchedFields[fieldErrorKey];
    if (!fieldTouched && !hasValidationAttempt) {
      return "";
    }

    if (!passenger.cprFiles || passenger.cprFiles.length === 0) {
      return translate("This field is required.", "هذا الحقل مطلوب.");
    }
    return "";
  };

  const handleShowSummary = () => {
    setShowSummaryModal(true);
  };

  const handleContinue = () => {
    // Mark that validation has been attempted
    setHasValidationAttempt(true);

    let allValid = true;
    const newFieldErrors = {};

    // Validate each passenger's fields
    for (let i = 0; i < passengers.length; i++) {
      const passenger = passengers[i];

      // Skip validation for logged-in user when "For Myself" is selected
      if (!isGuest && policyFor === "myself" && i === 0) {
        continue;
      }

      // Validate passenger fields and collect errors
      const passengerErrors = validatePassengerFields(passenger);

      // Add passenger-specific field errors
      Object.keys(passengerErrors).forEach((field) => {
        const fieldKey = `${field}_${i}`;
        newFieldErrors[fieldKey] = passengerErrors[field];
        allValid = false;
      });

      // Check CPR upload requirement
      if (!passenger.cprFiles || passenger.cprFiles.length === 0) {
        const fieldKey = `cpr_${i}`;
        newFieldErrors[fieldKey] = translate("This field is required.", "هذا الحقل مطلوب.");
        allValid = false;
      }
    }

    // Update field errors state to show validation messages
    setFieldErrors(newFieldErrors);

    if (allValid) {
      // Save contact details data to Redux
      const contactDetailsData = {
        passengers,
        policyFor,
      };

      savePersonalAccidentDataInRedux(contactDetailsData);
      handleNextSubstep();
    }
  };

  const renderLoggedInSection = () => {
    if (isGuest) return null;

    // Show loading state if profile data is not available yet
    if (!profileData) {
      return (
        <div className="mb-6">
          <div className="bg-[#E3F8FF] rounded-lg p-4 mb-4">
            <div className="animate-pulse [animation-duration:3s]">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      );
    }

    const userName = loggedInUserData.fullName || translate("User", "المستخدم");

    return (
      <div className="mb-4 mt-8 max767:px-5">
        <p className="text-[#48535B] text-md font-normal mb-2">
          {translate(
            <>
              You're logged in as <span className="font-bold">{userName}</span>. Choose whether to
              issue this policy under your name or someone else's.
            </>,
            <>
              أنت مسجل الدخول كـ <span className="font-bold">{userName}</span>. اختر ما إذا كنت
              تريد إصدار هذه الوثيقة باسمك أو باسم شخص آخر.
            </>
          )}
        </p>

        {/* Policy For Selection Tabs */}
        <div className={`${isLoadingQuote ? "opacity-50 pointer-events-none cursor-not-allowed" : ""}`}>
          <FormTabs
            label=""
            tabs={[
              {
                type: "myself",
                label_en: "For Myself",
                label_ar: "لنفسي",
              },
              {
                type: "someone_else",
                label_en: "For Someone Else",
                label_ar: "لشخص آخر",
              },
            ]}
            activeTab={policyFor}
            onTabClick={handlePolicyForChange}
          />
        </div>

        {/* Show logged-in user info table when "For Myself" is selected */}
        {policyFor === "myself" && (
          <div className="mt-6">
            {/* User Info Table - Clean design without headers */}
            <div className="mb-6">
              {profileData ? (
                <TravelDetailsTable
                  travelData={buildLoggedInUserTravelData()}
                  className="!bg-[#F6F8F9] max767:bg-white !border-none max767:!border max767:border-[#E5E7EB] rounded-[16px] p-4 max767:p-0 overflow-hidden"
                  innerClassName="border border-[#E5E7EB] rounded-[10px]"
                />
              ) : (
                <div className="animate-pulse [animation-duration:3s] space-y-3">
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPassengerForm = (passenger, index) => {
    return (
      <div key={passenger.id} className="mb-4 max767:px-5">
        {/* No headers needed - personal accident insurance doesn't use passenger terminology */}

        {/* CPR Upload */}
        <div className="mb-3">
          <label className="mb-1 text-[#48535B] block text-base">
            {translate(
              "Upload CPR (front & back)",
              "تحميل الهوية الوطنية (من الأمام والخلف)"
            )}
          </label>
          <FileUploadInput
            name={`cpr_passenger_${passenger.id}`}
            onFileSelect={(fileData) => handleFileSelect(fileData, index)}
            onFileRemove={() => handleFileRemove(index)}
            onLoseFocus={() =>
              setTouchedFields((prev) => ({ ...prev, [`cpr_${index}`]: true }))
            }
            validTypes={["image/jpeg", "image/png", "application/pdf"]}
            maxSizeKB={5000}
            uploadToServer={true}
            multiple={true}
            maxFiles={2}
            disabled={passenger.cprProcessing}
            initialFiles={passenger.cprFiles || []}
          />

          {/* CPR Processing Status */}
          {passenger.cprProcessing && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-sm text-blue-700">
                  {translate(
                    "Processing CPR data...",
                    "جاري معالجة بيانات الهوية..."
                  )}
                </p>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                {translate(
                  "Please wait while we extract your information...",
                  "يرجى الانتظار بينما نستخرج معلوماتك..."
                )}
              </p>
            </div>
          )}

          {/* Show inline error for CPR upload */}
          {getCprUploadError(index) && (
            <div className="mt-2 text-red-600 text-sm">
              {getCprUploadError(index)}
            </div>
          )}
        </div>

        {/* CPR Number */}
        <div className="mb-3">
          <Input
            id={`cprNumber_${passenger.id}`}
            label={translate("CPR Number", "رقم الهوية الوطنية")}
            type="text"
            value={passenger.cprNumber}
            handleChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // Only allow digits
              handleInputChange(index, "cprNumber", value);
            }}
            error={getFieldError(index, "cprNumber")}
            disabled={passenger.cprProcessing}
            maxLength={9}
          />
        </div>

        {/* Full Name */}
        <div className="mb-3">
          <Input
            id={`fullName_${passenger.id}`}
            label={translate("Full Name", "الاسم الكامل")}
            type="text"
            value={passenger.fullName}
            handleChange={(e) =>
              handleInputChange(index, "fullName", e.target.value)
            }
            error={getFieldError(index, "fullName")}
            disabled={passenger.cprProcessing}
          />
        </div>

        {/* Email Address */}
        <div className="mb-3">
          <Input
            id={`emailAddress_${passenger.id}`}
            label={translate("Email Address", "عنوان البريد الإلكتروني")}
            type="email"
            value={passenger.emailAddress}
            handleChange={(e) =>
              handleInputChange(index, "emailAddress", e.target.value)
            }
            error={getFieldError(index, "emailAddress")}
          />
        </div>

        {/* Mobile Number */}
        <div className="mb-3">
          <TelInput
            name="mobileNumber"
            value={passenger.mobileNumber}
            error={getFieldError(index, "mobileNumber")}
            onChange={(e) =>
              handleInputChange(index, e.target.name, e.target.value)
            }
            label={translate("Mobile Number", "رقم الهاتف المحمول")}
          />
        </div>

        {/* Nationality */}
        <div className="mb-3">
          <SelectInput
            name="nationality"
            formData={passenger}
            label={translate("Nationality", "الجنسية")}
            options_data={nationalityOptions || []}
            error={getFieldError(index, "nationality")}
            handleChange={(e) =>
              !passenger.cprProcessing &&
              handleInputChange(index, e.target.name, e.target.value)
            }
            default_placeholder={translate(
              "Please select an option.",
              "يرجى اختيار خيار."
            )}
            disabled={passenger.cprProcessing || nationalitiesLoading}
          />
        </div>

        {/* Gender */}
        <div
          className={`mb-3 ${
            passenger.cprProcessing ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <FormTabs
            label={translate("Gender", "الجنس")}
            tabs={genderTabs}
            onTabClick={(type) =>
              !passenger.cprProcessing &&
              handleInputChange(index, "gender", type)
            }
            activeTab={passenger.gender}
            error={getFieldError(index, "gender")}
          />
        </div>

        {/* Date of Birth */}
        <div className="date_of_birth mb-3">
          <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
            {translate("Date of Birth", "تاريخ الميلاد")}
          </label>

          {screenResolution.mobile ? (
            /* Mobile: Use SingleDatePicker with native date picker */
            <SingleDatePicker
              value={passenger.dateOfBirth.fullDate || ""}
              onChange={(dateString) => handleMobileDatePickerSelect(index, dateString)}
              placeholder={translate("Select Date", "اختر التاريخ")}
              error={
                getFieldError(index, "dateOfBirth.day") ||
                getFieldError(index, "dateOfBirth.month") ||
                getFieldError(index, "dateOfBirth.year")
              }
              useNativeMobile={true}
              disabled={passenger.cprProcessing}
            />
          ) : (
            /* Desktop: Keep existing select dropdowns */
            <>
              <div className="flex gap-6 self-stretch">
                <SelectInput
                  name="day"
                  options_data={daysOptions}
                  formData={{ day: passenger.dateOfBirth.day || "" }}
                  default_placeholder={translate("Day", "يوم")}
                  error=""
                  handleChange={(e) => {
                    const day = e.target.value;
                    const month = passenger.dateOfBirth.month;
                    const year = passenger.dateOfBirth.year;
                    const fullDate =
                      day && month && year
                        ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                            2,
                            "0"
                          )}`
                        : "";
                    // Update state
                    const updatedDOB = {
                      day,
                      month,
                      year,
                      fullDate,
                    };
                    handleInputChange(index, "dateOfBirth", updatedDOB);

                    // Validate date of birth in real-time when complete date is provided
                    if (day && month && year) {
                      const updatedPassenger = {
                        ...passengers[index],
                        dateOfBirth: updatedDOB,
                      };
                      const validationErrors = validatePassengerFields(updatedPassenger);
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        newErrors[`dateOfBirth.day_${index}`] = validationErrors["dateOfBirth.day"] || "";
                        return newErrors;
                      });
                    }
                  }}
                  disabled={passenger.cprProcessing}
                  maxMenuHeight={185}
                />

                <SelectInput
                  name="month"
                  options_data={monthsOptions}
                  formData={{ month: passenger.dateOfBirth.month || "" }}
                  default_placeholder={translate("Month", "شهر")}
                  error=""
                  handleChange={(e) => {
                    const month = e.target.value;
                    const day = passenger.dateOfBirth.day;
                    const year = passenger.dateOfBirth.year;
                    const fullDate =
                      day && month && year
                        ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                            2,
                            "0"
                          )}`
                        : "";
                    // Update state
                    const updatedDOB = {
                      day,
                      month,
                      year,
                      fullDate,
                    };
                    handleInputChange(index, "dateOfBirth", updatedDOB);

                    // Validate date of birth in real-time when complete date is provided
                    if (day && month && year) {
                      const updatedPassenger = {
                        ...passengers[index],
                        dateOfBirth: updatedDOB,
                      };
                      const validationErrors = validatePassengerFields(updatedPassenger);
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        newErrors[`dateOfBirth.day_${index}`] = validationErrors["dateOfBirth.day"] || "";
                        return newErrors;
                      });
                    }
                  }}
                  disabled={passenger.cprProcessing}
                  maxMenuHeight={185}
                />

                <SelectInput
                  name="year"
                  options_data={yearsOptions}
                  formData={{ year: passenger.dateOfBirth.year || "" }}
                  default_placeholder={translate("Year", "سنة")}
                  error=""
                  handleChange={(e) => {
                    const year = e.target.value;
                    const day = passenger.dateOfBirth.day;
                    const month = passenger.dateOfBirth.month;
                    const fullDate =
                      day && month && year
                        ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                            2,
                            "0"
                          )}`
                        : "";
                    // Update state
                    const updatedDOB = {
                      day,
                      month,
                      year,
                      fullDate,
                    };
                    handleInputChange(index, "dateOfBirth", updatedDOB);

                    // Validate date of birth in real-time when complete date is provided
                    if (day && month && year) {
                      const updatedPassenger = {
                        ...passengers[index],
                        dateOfBirth: updatedDOB,
                      };
                      const validationErrors = validatePassengerFields(updatedPassenger);
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        newErrors[`dateOfBirth.day_${index}`] = validationErrors["dateOfBirth.day"] || "";
                        return newErrors;
                      });
                    }
                  }}
                  disabled={passenger.cprProcessing}
                  maxMenuHeight={185}
                />
              </div>
              {(getFieldError(index, "dateOfBirth.day") ||
                getFieldError(index, "dateOfBirth.month") ||
                getFieldError(index, "dateOfBirth.year")) && (
                <div className="mt-2 text-red-600 text-sm">
                  {getFieldError(index, "dateOfBirth.day") ||
                    getFieldError(index, "dateOfBirth.month") ||
                    getFieldError(index, "dateOfBirth.year")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Get total premium for footer
  const totalPremium = quoteData?.data?.totalPremium
    ? parseFloat(quoteData.data.totalPremium)
    : 0;

  return (
    <div className="w-full">
      <PersonalAccidentSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        quoteData={quoteData}
      />
      <PersonalAccidentJourneyLayout is_hide_back_btn={false}>
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_disabled={isLoadingQuote || (policyFor === "someone_else" && !hasValidatedData)}
          btn_label={translate("Continue", "التالي")}
          isLoading={isLoadingQuote}
          show_summary_btn={!isLoadingQuote && !!quoteData && !(policyFor === "someone_else" && !hasValidatedData)}
          summary_price={
            !isLoadingQuote && quoteData
              ? (() => {
                  const currency = translate("BHD", "د.ب");
                  const formattedPrice = formatPremiumWithCurrency(
                    totalPremium,
                    "",
                    "en-US"
                  );
                  // For RTL, reverse the order (number first, currency after)
                  return isRTL
                    ? `${formattedPrice} ${currency}`
                    : `${currency} ${formattedPrice}`;
                })()
              : ""
          }
          summary_btn_text={translate("Show Summary", "عرض التسعيرة")}
          summary_btn_onClick={handleShowSummary}
        >
          <div className="flex flex-col items-center p-4 max767:p-0 mx-auto pt-0 py-4 pb-28">
            <div className="w-full max-w-[500px]">
              <div className="contactdetailstitle max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0">
                <h1 className="text-[#1A2024] text-center text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] max767:text-left mb-2 max767:rtl:text-right">
                  {translate("Your Information", "بياناتك")}
                </h1>
                <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-6 max767:text-left max767:rtl:text-right">
                  {translate(
                    "Fill in your information, and we'll take care of the rest.",
                    "يرجى إدخال المعلومات المطلوبة أدناه."
                  )}
                </p>
              </div>

              {renderLoggedInSection()}

              {/* Passengers Forms */}
              {passengers.map((passenger, index) => {
                // Skip rendering passenger 1 form when "For Myself" is selected for logged-in users
                if (!isGuest && policyFor === "myself" && index === 0) {
                  return null;
                }
                return renderPassengerForm(passenger, index);
              })}
            </div>
          </div>
        </BottomStickyBtnLayout>
      </PersonalAccidentJourneyLayout>
    </div>
  );
};

export default ContactDetails;
